import { create } from 'zustand';
import { jwtDecode } from 'jwt-decode';
import { authService } from '../api/authService';
import { setAuthTokenGetter } from '../api/client';
import { notifySuccess } from '../utils/notifications';
import type { AuthState, User } from '../types/auth.types';

const TOKEN_KEY = 'ark_portal_token';
const REDIRECT_KEY = 'ark_portal_redirect';

interface JwtPayload {
  sub: string;
  email: string;
  role: string;
  schoolId?: number;
  exp: number;
  iat: number;
}

// Decode a stored token synchronously so the menu renders with the correct role
// before checkAuth() resolves. Missing profile fields are filled in by checkAuth().
const decodeStoredToken = (token: string): User | null => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    if (decoded.exp <= Date.now() / 1000) return null;
    return {
      id: parseInt(decoded.sub, 10),
      firstName: '',
      lastName: '',
      email: decoded.email,
      role: decoded.role as User['role'],
      schoolId: decoded.schoolId ?? null,
      isActive: true,
    };
  } catch {
    return null;
  }
};

/**
 * Auth store for managing authentication state
 * Handles login, logout, token persistence, and JWT decoding
 * Automatically loads token from localStorage on initialization
 */
export const useAuthStore = create<AuthState>((set, get) => {
  const storedToken = typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
  const initialUser = storedToken ? decodeStoredToken(storedToken) : null;

  return {
    token: storedToken,
    user: initialUser,
    isAuthenticated: initialUser !== null,

    /**
     * Login with email and password
     * Stores token in localStorage and decodes user info from JWT
     * Restores intended destination after successful login
     */
    login: async (email: string, password: string) => {
      try {
        const response = await authService.login(email, password);
        const { token, user } = response;

        // Store token in localStorage
        localStorage.setItem(TOKEN_KEY, token);

        // Update state
        set({
          token,
          user,
          isAuthenticated: true,
        });
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || '';
        const status = error.response?.status || error.statusCode;
        const userRole = error.response?.data?.data?.userRole || null;

        const isSchoolError =
          status === 403 &&
          (errorMessage.toLowerCase().includes('suspended') ||
           errorMessage.toLowerCase().includes('deactivated') ||
           errorMessage.toLowerCase().includes('trial has ended'));

        // Always clear the stored token to prevent stale tokens from affecting future sessions
        localStorage.removeItem(TOKEN_KEY);

        if (isSchoolError) {
          // Store partial user with role so status pages can show role-specific content
          set({
            token: null,
            user: userRole ? ({ role: userRole } as any) : null,
            isAuthenticated: false,
          });
        } else {
          set({
            token: null,
            user: null,
            isAuthenticated: false,
          });
        }
        throw error;
      }
    },

    /**
     * Logout and clear all auth state
     * Removes token from localStorage and clears all cached data
     * Resets all stores to initial state
     * Redirects to login page and shows success notification
     */
    logout: () => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REDIRECT_KEY);
      sessionStorage.removeItem('ark_status_role');
      
      // Clear all cached data on logout
      // Import is done dynamically to avoid circular dependency
      import('./cacheStore').then(({ useCacheStore }) => {
        useCacheStore.getState().clear();
      });

      // The next person to sign in on this browser starts on the current term.
      import('./termStore').then(({ useTermStore }) => {
        useTermStore.getState().reset();
      });
      
      // Reset all stores to initial state
      import('./studentStore').then(({ useStudentStore }) => {
        useStudentStore.setState({
          students: [],
          selectedStudent: null,
          loading: false,
          error: null,
        });
      });
      
      import('./paymentStore').then(({ usePaymentStore }) => {
        usePaymentStore.setState({
          payments: [],
          arrears: [],
          loading: false,
          error: null,
        });
      });
      
      import('./attendanceStore').then(({ useAttendanceStore }) => {
        useAttendanceStore.setState({
          attendanceRecords: [],
          report: null,
          loading: false,
          error: null,
        });
      });
      
      import('./expenseStore').then(({ useExpenseStore }) => {
        useExpenseStore.setState({
          expenses: [],
          loading: false,
          error: null,
        });
      });
      
      import('./referenceStore').then(({ useReferenceStore }) => {
        useReferenceStore.setState({
          classes: [],
          levels: [],
          terms: [],
          loading: false,
          error: null,
        });
      });

      // Both are module-level singletons. Left unreset they carried the previous school's
      // invoice summary into the next login, which is what flashed a billing banner at a
      // school that has no invoices at all.
      import('./subscriptionPaymentStore').then(({ useSubscriptionPaymentStore }) => {
        useSubscriptionPaymentStore.setState({
          summary: null,
          loadingSummary: false,
        });
      });

      import('./classPaymentStore').then(({ useClassPaymentStore }) => {
        useClassPaymentStore.getState().reset();
      });

      import('./enrollmentStore').then(({ useEnrollmentStore }) => {
        useEnrollmentStore.setState({
          activeLink: null,
          allLinks: [],
          pendingSubmissions: [],
          loading: false,
          error: null,
        });
      });
      
      // Clear auth state
      set({
        token: null,
        user: null,
        isAuthenticated: false,
      });
      
      // Show success notification
      notifySuccess('Logged out successfully', 'You have been logged out of your account');
    },

    /**
     * Check authentication status
     * Validates token and fetches current user from API
     * Called on app initialization and by protected routes
     */
    checkAuth: async () => {
      const { token } = get();

      if (!token) {
        set({
          token: null,
          user: null,
          isAuthenticated: false,
        });
        return;
      }

      try {
        // Decode JWT to check expiration
        const decoded = jwtDecode<JwtPayload>(token);
        const currentTime = Date.now() / 1000;

        // Check if token is expired (with 5 second buffer)
        if (decoded.exp <= currentTime + 5) {
          console.log('Token expired, logging out...');
          localStorage.removeItem(TOKEN_KEY);
          set({
            token: null,
            user: null,
            isAuthenticated: false,
          });
          return;
        }

        // Fetch current user from API to validate token
        const user = await authService.getCurrentUser();

        set({
          user,
          isAuthenticated: true,
        });
      } catch (error: any) {
        console.error('Auth check failed:', error);
        // Token is invalid or API call failed, clear auth state
        localStorage.removeItem(TOKEN_KEY);
        set({
          token: null,
          user: null,
          isAuthenticated: false,
        });
      }
    },

    /**
     * Store intended destination URL before redirecting to login
     * Used to restore user's location after successful authentication
     */
    storeIntendedDestination: (path: string) => {
      if (path && path !== '/login') {
        localStorage.setItem(REDIRECT_KEY, path);
      }
    },

    /**
     * Get and clear stored intended destination
     * Returns the path user was trying to access before login
     */
    getAndClearIntendedDestination: (): string | null => {
      const path = localStorage.getItem(REDIRECT_KEY);
      if (path) {
        localStorage.removeItem(REDIRECT_KEY);
        return path;
      }
      return null;
    },
  };
});

// Set up API client to use auth store for token
setAuthTokenGetter(
  () => useAuthStore.getState().token,
  () => useAuthStore.getState().logout()
);
